import dayjs from "dayjs";

import { AccountDetailsStatusI } from "shared/lib/interfaces/account";
import { ACCOUNT_DISPOSITION_STATUSES } from "shared/lib/constants/account";
import {
  ACCOUNT_DISQUALIFY_DISPOSITIONS,
  DISPOSITION_SPECIFICS,
  PROSPECT_DISPOSITION_TYPES,
} from "shared/lib/constants/dispositions";
import { getCanonicalMeetingTime } from "shared/lib/helpers/canonical-meeting-time";

/**
 * GLE-8230: a disqualify cancels the upcoming meeting it takes down, and the
 * prospect gets a cancellation email for it. These rules mirror the backend's
 * (glen/server/meeting_cancellation.go) so the rep is warned exactly when a
 * meeting is about to be cancelled: a disqualified lead takes its own meeting,
 * a disqualified account takes every meeting.
 */
export const DQ_CANCEL_SCOPES = {
  NONE: "none",
  CONTACT: "contact",
  ACCOUNT: "account",
} as const;

export type DQCancelScopeI =
  (typeof DQ_CANCEL_SCOPES)[keyof typeof DQ_CANCEL_SCOPES];

const DQ_PROSPECT_TYPES: string[] = [
  PROSPECT_DISPOSITION_TYPES.NOT_RELEVANT,
  PROSPECT_DISPOSITION_TYPES.BAD_DATA,
  PROSPECT_DISPOSITION_TYPES.DO_NOT_CALL,
];

const DQ_CONTACT_DISPOSITIONS: string[] = [
  DISPOSITION_SPECIFICS.NOT_QUALIFIED_AUTHORITY,
  DISPOSITION_SPECIFICS.DATA_WRONG,
  DISPOSITION_SPECIFICS.DO_NOT_CALL,
  DISPOSITION_SPECIFICS.OTHER_DO_NOT_FOLLOW_UP,
];

const MEETING_TIME_FORMAT = "MMM D, hh:mm A";

/**
 * The prospect types that disqualify the lead they are set on.
 */
export const isDisqualifyingProspectType = (
  prospectType: string | undefined
): boolean => !!prospectType && DQ_PROSPECT_TYPES.includes(prospectType);

/**
 * "Qualified or unqualified meeting" says the account already had its
 * meeting; it never cancels one still ahead.
 */
export const getDisqualifyModalCancelScope = ({
  isAccountSelected,
  disposition,
}: {
  isAccountSelected: boolean;
  disposition: string;
}): DQCancelScopeI => {
  if (
    disposition ===
    ACCOUNT_DISQUALIFY_DISPOSITIONS.QUALIFIED_OR_UNQUALIFIED_MEETING
  ) {
    return DQ_CANCEL_SCOPES.NONE;
  }

  return isAccountSelected
    ? DQ_CANCEL_SCOPES.ACCOUNT
    : DQ_CANCEL_SCOPES.CONTACT;
};

/**
 * The contact types the backend turns into a disqualify of the lead.
 */
export const getContactTypeCancelScope = (
  contactType: string | undefined
): DQCancelScopeI =>
  isDisqualifyingProspectType(contactType)
    ? DQ_CANCEL_SCOPES.CONTACT
    : DQ_CANCEL_SCOPES.NONE;

/**
 * Same order the dialer endpoint uses: no conversation is a voicemail, a
 * disqualifying prospect type wins, otherwise the disposition decides.
 */
export const getDispositionFormCancelScope = ({
  isConversation,
  prospectType,
  dispositionKey,
}: {
  isConversation: boolean | undefined;
  prospectType: string | undefined;
  dispositionKey: string | undefined;
}): DQCancelScopeI => {
  if (!isConversation) {
    return DQ_CANCEL_SCOPES.NONE;
  }

  if (isDisqualifyingProspectType(prospectType)) {
    return DQ_CANCEL_SCOPES.CONTACT;
  }

  if (dispositionKey === DISPOSITION_SPECIFICS.QC_NOT_MET_FOR_ACCOUNT) {
    return DQ_CANCEL_SCOPES.ACCOUNT;
  }

  if (dispositionKey && DQ_CONTACT_DISPOSITIONS.includes(dispositionKey)) {
    return DQ_CANCEL_SCOPES.CONTACT;
  }

  return DQ_CANCEL_SCOPES.NONE;
};

/**
 * Who holds the live meeting. The backend lists them (empty when none); the
 * account's primary contact is only a fallback for a response without the
 * list, since it is set independently of the booking and can point at someone
 * else.
 */
const getMeetingContactIds = (accountStatus: AccountDetailsStatusI) => {
  if (accountStatus.meeting_contact_ids) {
    return accountStatus.meeting_contact_ids;
  }

  return accountStatus.primary_contact_id
    ? [accountStatus.primary_contact_id]
    : [];
};

/**
 * The line to show the rep before they disqualify, or null when the disqualify
 * cancels nothing. A lead-level disqualify only touches the meeting when that
 * lead is the one the meeting is with.
 */
export const getMeetingCancelNotice = ({
  accountStatus,
  scope,
  contactId,
}: {
  accountStatus: AccountDetailsStatusI | undefined;
  scope: DQCancelScopeI;
  contactId: string | undefined;
}): string | null => {
  if (
    scope === DQ_CANCEL_SCOPES.NONE ||
    accountStatus?.status !== ACCOUNT_DISPOSITION_STATUSES.MEETING_SCHEDULED
  ) {
    return null;
  }

  const meetingTime = getCanonicalMeetingTime(accountStatus);

  if (!meetingTime || !dayjs(meetingTime).isAfter(dayjs())) {
    return null;
  }

  // Only the rep's own meetings are ever cancelled; the backend lists them.
  const meetingContactIds = getMeetingContactIds(accountStatus);

  if (
    meetingContactIds.length === 0 ||
    (scope === DQ_CANCEL_SCOPES.CONTACT &&
      (!contactId || !meetingContactIds.includes(contactId)))
  ) {
    return null;
  }

  return `This will also cancel the upcoming meeting on ${dayjs(
    meetingTime
  ).format(MEETING_TIME_FORMAT)}.`;
};
